<template>
  <div class="container py-5">
    <!-- Section profil -->
    <div class="alert" :class="getProfileAlertClass()" role="alert">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h4 class="alert-heading mb-2">{{ getProfileMessage() }}</h4>
          <p class="mb-0">{{ getProfileDescription() }}</p>
        </div>
        <router-link 
          v-if="!user.has_completed_profile" 
          to="/profile" 
          class="btn btn-primary"
        >
          Compléter mon profil
        </router-link>
      </div>
    </div>

    <div class="row mt-4">
      <!-- Section offres d'emploi -->
      <div class="col-md-4">
        <div class="card">
          <div class="card-header bg-primary text-white d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Offres d'emploi</h5>
            <router-link to="/" class="btn btn-sm btn-light">
              <i class="fas fa-home"></i> Accueil
            </router-link>
          </div>
          <div class="card-body">
            <div v-if="loading" class="text-center">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Chargement...</span>
              </div>
            </div>
            
            <div v-else-if="jobOffers.length === 0" class="text-center">
              <p>Aucune offre disponible pour le moment.</p>
            </div>
            
            <div v-else class="job-offers-list">
              <div v-for="offer in jobOffers" :key="offer.id" class="job-offer-item mb-3">
                <h6>{{ offer.title }}</h6>
                <p class="text-muted small mb-2">
                  <i class="fas fa-calendar-alt me-2"></i>
                  Date limite : {{ formatDate(offer.deadline) }}
                </p>
                <!-- Show description as plain text, not raw HTML -->
                <p class="small mb-2">{{ truncateDescription(stripHtmlTags(offer.description)) }}</p>
                <router-link 
                  :to="`/jobs/${offer.id}/apply`"
                  class="btn btn-sm btn-outline-primary"
                >
                  Postuler
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Section candidatures -->
      <div class="col-md-8">
        <ApplicationsList />
      </div>
    </div>
  </div>
</template>

<script>
import ApplicationsList from '@/components/applications/ApplicationsList.vue'

export default {
  name: 'Status',
  
  components: {
    ApplicationsList
  },

  data() {
    return {
      user: {},
      jobOffers: [],
      loading: true
    }
  },

  async created() {
    await Promise.all([
      this.loadProfile(),
      this.loadJobOffers()
    ])
  },

  methods: {
    async loadProfile() {
      try {
        const response = await this.$http.get('/profile')
        this.user = response.data.profile
      } catch (error) {
        console.error('Erreur lors du chargement du profil:', error)
      }
    },

    async loadJobOffers() {
      try {
        const response = await this.$http.get('/jobs')
        this.jobOffers = response.data.job_offers.data.filter(offer => 
          offer.is_active && !this.isExpired(offer.deadline)
        )
      } catch (error) {
        console.error('Erreur lors du chargement des offres:', error)
      } finally {
        this.loading = false
      }
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },

    isExpired(date) {
      return new Date(date) < new Date()
    },

    // This method removes all HTML tags to safely display description as text only
    stripHtmlTags(html) {
      if (!html) return ''
      const div = document.createElement('div')
      div.innerHTML = html
      return div.textContent || div.innerText || ''
    },

    truncateDescription(description) {
      if (!description) return ''
      return description.length > 100 
        ? description.substring(0, 100) + '...' 
        : description
    },

    getProfileAlertClass() {
      return this.user.has_completed_profile 
        ? 'alert-success' 
        : 'alert-warning'
    },

    getProfileMessage() {
      return this.user.has_completed_profile 
        ? 'Profil complété !'
        : 'Profil incomplet'
    },

    getProfileDescription() {
      return this.user.has_completed_profile 
        ? 'Vous pouvez maintenant postuler aux offres d\'emploi.'
        : 'Veuillez compléter votre profil pour avoir plus de chance'
    }
  }
}
</script>

<style scoped>
.job-offer-item {
  padding: 15px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.job-offer-item:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.job-offers-list {
  max-height: 600px;
  overflow-y: auto;
  padding-right: 10px;
}

.job-offers-list::-webkit-scrollbar {
  width: 6px;
}

.job-offers-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.job-offers-list::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.job-offers-list::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
